import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams, usePathname } from 'expo-router';
import { theme } from '@/constants/theme';
import { RuntimeSessionGuard } from '@/features/unified-learning/runtime/screens/RuntimeSessionGuard';
import {
  buildRuntimeExamPath,
  buildRuntimeHomePath,
  buildRuntimePracticePath,
  buildRuntimeReviewPath,
  buildRuntimeReversePath,
  resolveRuntimeBase,
} from '@/features/unified-learning/runtime/runtimeRoutes';

const BG = '#0B0B14';
const SURFACE = '#16142A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#9896B4';
const PRIMARY = theme.colors.primary;

export function GapSessionResultScreen() {
  const { slug, uid, token, total, answered, mastery, improved, weak, risky, xp } = useLocalSearchParams<{
    slug: string;
    uid: string;
    token?: string;
    total?: string;
    answered?: string;
    mastery?: string;
    improved?: string;
    weak?: string;
    risky?: string;
    xp?: string;
  }>();

  const runtimeBase = resolveRuntimeBase(usePathname());
  const safeSlug = typeof slug === 'string' ? slug : '';
  const safeUid = typeof uid === 'string' ? uid : '';
  const safeToken = typeof token === 'string' ? token : undefined;
  const runtimeSession = { uid: safeUid, token: safeToken };

  const masteryRate = Number(mastery ?? 0);
  const weakCount = Number(weak ?? 0);
  const riskyCount = Number(risky ?? 0);
  const masteryText = masteryRate >= 75 ? 'התאוששות חזקה' : masteryRate >= 50 ? 'התאוששות חלקית' : 'צריך עוד סשן חיזוק';
  const masteryTone = masteryRate >= 75 ? '#34D399' : masteryRate >= 50 ? '#FBBF24' : '#F87171';
  const recommendedNextStep = masteryRate >= 75 ? 'exam' : weakCount + riskyCount >= 2 ? 'reverse' : 'practice';

  if (!safeSlug || !safeUid) {
    return <RuntimeSessionGuard runtimeBase={runtimeBase} slug={safeSlug} />;
  }

  return (
    <ScrollView style={s.root} contentContainerStyle={s.content}>
      <Text style={s.eyebrow}>KURSI LEARN · תוצאות חזרה</Text>
      <Text style={s.title}>תוצאות סשן סגירת פערים</Text>

      <View style={[s.readinessCard, { borderColor: masteryTone + '66' }]}>
        <Text style={[s.readinessTitle, { color: masteryTone }]}>{masteryText}</Text>
        <Text style={s.readinessBody}>שיעור שליטה: {masteryRate}%</Text>
      </View>

      <View style={s.card}>
        <Text style={s.cardTitle}>סיכום הסשן</Text>
        <Text style={s.row}>נענו: {Number(answered ?? 0)}/{Number(total ?? 0)}</Text>
        <Text style={s.row}>תשובות שהשתפרו: {Number(improved ?? 0)}</Text>
        <Text style={s.row}>בחירות חלשות: {weakCount} · מסוכנות: {riskyCount}</Text>
        <Text style={s.row}>XP שהרווחת: {Number(xp ?? 0)}</Text>
      </View>

      <View style={s.actionGroup}>
        {recommendedNextStep === 'exam' && (
          <Pressable
            style={s.primaryBtn}
            onPress={() => router.push(buildRuntimeExamPath(runtimeBase, safeSlug, runtimeSession) as any)}
          >
            <Text style={s.primaryBtnText}>המשך לבדיקת מוכנות</Text>
          </Pressable>
        )}

        {recommendedNextStep === 'reverse' && (
          <Pressable
            style={s.primaryBtn}
            onPress={() => router.push(buildRuntimeReversePath(runtimeBase, safeSlug, runtimeSession) as any)}
          >
            <Text style={s.primaryBtnText}>פתח עוד סשן סגירת פערים</Text>
          </Pressable>
        )}

        {recommendedNextStep === 'practice' && (
          <Pressable
            style={s.primaryBtn}
            onPress={() => router.push(buildRuntimePracticePath(runtimeBase, safeSlug, runtimeSession) as any)}
          >
            <Text style={s.primaryBtnText}>חזרה לתרגול ממוקד</Text>
          </Pressable>
        )}

        <Pressable
          style={s.secondaryBtn}
          onPress={() => router.push(buildRuntimePracticePath(runtimeBase, safeSlug, runtimeSession) as any)}
        >
          <Text style={s.secondaryBtnText}>פתח מרכז תרגול</Text>
        </Pressable>

        <Pressable
          style={s.secondaryBtn}
          onPress={() => router.push(buildRuntimeReviewPath(runtimeBase, safeSlug, runtimeSession) as any)}
        >
          <Text style={s.secondaryBtnText}>פתח סקירת טעויות</Text>
        </Pressable>

        <Pressable
          style={s.homeBtn}
          onPress={() => router.replace(buildRuntimeHomePath(runtimeBase, safeSlug, runtimeSession) as any)}
        >
          <Text style={s.homeBtnText}>חזרה לבית המסלול</Text>
        </Pressable>
      </View>
    </ScrollView>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  content: { padding: 20, gap: 14, maxWidth: 700, width: '100%', alignSelf: 'center' as any, paddingTop: 42, paddingBottom: 40 },
  eyebrow: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 12, letterSpacing: 1 },
  title: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 30, textAlign: 'right' },
  readinessCard: { backgroundColor: SURFACE, borderWidth: 1, borderRadius: 14, padding: 14, gap: 8 },
  readinessTitle: { fontFamily: theme.fonts.bold, fontSize: 20, textAlign: 'right' },
  readinessBody: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right' },
  card: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 8 },
  cardTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 18, textAlign: 'right' },
  row: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right' },
  actionGroup: { gap: 8 },
  primaryBtn: { backgroundColor: PRIMARY, borderRadius: 12, alignItems: 'center', paddingVertical: 12 },
  primaryBtnText: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 16 },
  secondaryBtn: { borderWidth: 1, borderColor: BORDER, borderRadius: 12, alignItems: 'center', paddingVertical: 11 },
  secondaryBtnText: { color: TEXT, fontFamily: theme.fonts.bold },
  homeBtn: { borderRadius: 12, alignItems: 'center', paddingVertical: 11 },
  homeBtnText: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14 },
});
